import { dimensionKeys, type DimensionKey } from './dimensions';
import type { MentionRow } from './generator';

/**
 * The filter predicate: chip selections in, surviving rows out.
 *
 * A `Query` holds one `Set` per dimension, or `null` for "this dimension does
 * not constrain". Sets rather than arrays because the predicate runs once per
 * row per dimension — `Array.includes` over a 20-item topic list would make the
 * inner loop 20x longer for no reason.
 */
export type Query = Readonly<Record<DimensionKey, ReadonlySet<string> | null>>;

export type Selections = Readonly<Partial<Record<DimensionKey, readonly string[]>>>;

/**
 * Build a `Query` from the chips' selections.
 *
 * **An emptied chip matches nothing** (2026-09-16, reversing the rule below).
 * Unticking every option in a filter has to empty the dashboard, because that
 * is what the control says it does: a user who has deselected every channel has
 * asked to see no channels, and answering with the unfiltered corpus makes the
 * chip look broken — the counter reads 0 and the chart does not move.
 *
 * > *Previously:* "Empty selection means unfiltered, not match nothing… `Clear`
 * > has to show all the data rather than emptying the dashboard." That reading
 * > protected a `Clear` control which was never built — `clearSelections` is
 * > declared in the reducer and dispatched by nothing. If one lands later it
 * > should **reset to every option selected**, which is the app's own opening
 * > state, rather than reach for the empty array this function now reads
 * > literally.
 *
 * `undefined` and `[]` are therefore no longer the same thing. Absent means the
 * chip has no opinion and the dimension goes unconstrained; an empty array is
 * the user having emptied it, and becomes an empty `Set` — which matches
 * nothing, with no special case needed. The app always supplies all six keys
 * (`initialSelections`), so only the second case occurs in practice; the
 * distinction is there so a partial `Selections` in a test cannot silently mean
 * "show nothing".
 *
 * `dimensions` is the list the module actually queries on. It is deliberately
 * separate from which chips are *rendered*: a module that hides the Brands chip
 * must not silently keep filtering by an invisible brand selection — and under
 * this rule getting that wrong would blank the page rather than merely
 * under-report it.
 */
export function toQuery(
  selections: Selections,
  dimensions: readonly DimensionKey[] = dimensionKeys,
): Query {
  const query = {} as Record<DimensionKey, ReadonlySet<string> | null>;
  for (const key of dimensionKeys) {
    const selected = selections[key];
    const constrains = dimensions.includes(key) && selected !== undefined;
    /* An empty `Set` matches nothing all by itself — see the note above. */
    query[key] = constrains ? new Set(selected) : null;
  }
  return query;
}

/**
 * Rows matching every constraint.
 *
 * Written as an explicit loop over a fixed key list rather than
 * `dimensionKeys.every(...)`: this is the hottest code in the app — it runs on
 * every chip toggle, over the slice the date index already narrowed — and a
 * closure allocation per row per dimension is the one cost worth avoiding here.
 */
export function filterRows(rows: readonly MentionRow[], query: Query): MentionRow[] {
  const out: MentionRow[] = [];
  for (const row of rows) {
    if (query.brand && !query.brand.has(row.brand)) continue;
    if (query.channel && !query.channel.has(row.channel)) continue;
    if (query.intent && !query.intent.has(row.intent)) continue;
    if (query.topic && !query.topic.has(row.topic)) continue;
    if (query.conversation && !query.conversation.has(row.conversation)) continue;
    if (query.sentiment && !query.sentiment.has(row.sentiment)) continue;
    out.push(row);
  }
  return out;
}

/**
 * A scalar that changes exactly when the query does — the Level-2 memo key.
 *
 * Selections are **sorted** before joining, so picking A then B and picking B
 * then A produce the same key and do not invalidate the memo for no reason.
 */
export function queryKey(selections: Selections, dimensions: readonly DimensionKey[]): string {
  return dimensions
    .map((key) => {
      const selected = selections[key];
      /*
       * `*` for absent, so it cannot collide with the empty string an emptied
       * chip produces. The two now mean opposite things — unconstrained versus
       * match-nothing — and a memo key that could not tell them apart would
       * serve one's result for the other.
       */
      return `${key}:${selected === undefined ? '*' : [...selected].sort().join(',')}`;
    })
    .join('|');
}
